<template>
  <multi-selector-template
    ref="modalRef"
    :title="finalTitle"
    :primary-color="finalPrimaryColor"
    :selected-items="localSelectedUsers"
    :clearable="finalClearable"
    :clear-text="standardNaming.CLEAR_TEXT"
    :clear-aria-label="'Clear all selected users'"
    :show-find="true"
    :auto-focus-find="true"
    :show-select-all="false"
    :show-badge-count="true"
    :show-archived-toggle="finalShowArchivedToggle"
    :include-archived="includeInactiveUsers"
    :show-none-option="currentNoItemOption"
    :none-selected="localHasNoUserSelected"
    :show-row2="true"
    :show-footer-controls="
      finalShowArchivedToggle || currentNoItemOption || showSubItemsCheckbox
    "
    :show-footer="true"
    :primary-action-text="finalActionText"
    :secondary-action-text="withShortcut('Cancel', shortcuts.CANCEL)"
    :loading="isLoading"
    width="xl"
    @show="onShow"
    @hide="onHide"
    @shown="onShown"
    @clear="clear"
    @search="handleSearch"
    @save="confirmSelection"
    @cancel="handleCancel"
    @focus-first="handleFocusFirst"
    @focus-last="handleFocusLast"
    @focus-body-last="handleFocusBodyLast"
    @toggle-archived="handleToggleArchived"
    @select-none="handleSelectNone"
    @toggle-subtasks="handleToggleSubtasks"
  >
    <template #left>
      <user-browser
        ref="userBrowserRef"
        mode="multi"
        :selected-items="localSelectedUsers"
        :selected-ids="localSelectedIds"
        :primary-color="finalPrimaryColor"
        :include-archived="includeInactiveUsers"
        :hide-footer="true"
        :team-ids="teamIds"
        @item-selected="handleUserSelected"
        @item-toggled="handleUserToggled"
      />
    </template>

    <template #right>
      <!-- Empty state when no selections -->
      <EmptySelectionState
        v-if="!localSelectedUsers.length && !localHasNoUserSelected"
      />

      <!-- Content wrapper for scrolling (when there are items) -->
      <div v-else>
        <!-- No User Option -->
        <div
          v-if="shouldShowNoUserOption && localHasNoUserSelected"
          class="item-selector__content-item tw-flex tw-items-center tw-p-3"
        >
          <q-icon name="person_off" class="tw-mr-2 tw-text-gray-medium" />
          <div class="tw-flex-1">
            <span class="tw-text-sm">{{ currentNoItemText }}</span>
          </div>
          <octicon
            icon="x"
            class="tw-cursor-pointer tw-text-red hover:tw-opacity-80"
            :height="16"
            @click="localHasNoUserSelected = false"
          />
        </div>

        <!-- Selected users list -->
        <div v-if="localSelectedUsers.length">
          <div
            v-for="user in localSelectedUsers"
            :key="user.userId"
            class="item-selector__content-item tw-flex tw-items-center tw-p-3"
          >
            <!-- User Avatar -->
            <user-avatar
              :member="user"
              size="20px"
              class="tw-mr-3 tw-shrink-0"
              :hide-inactive-warning="isUserArchived(user)"
            />

            <!-- User info -->
            <div class="tw-flex tw-min-w-0 tw-grow tw-flex-col">
              <span class="tw-text-sm tw-font-medium">
                {{ getUserDisplayName(user) }}
              </span>
            </div>

            <!-- Archived badge -->
            <span
              v-if="isUserArchived(user)"
              class="tw-ml-2 tw-inline-flex tw-items-center tw-rounded-full tw-px-2 tw-py-0.5 tw-text-xs tw-font-medium tw-text-red"
              :style="{ backgroundColor: 'rgb(239 68 68 / 0.2)' }"
            >
              Archived
            </span>

            <!-- Remove button -->
            <octicon
              icon="x"
              class="tw-ml-2 tw-cursor-pointer tw-text-red hover:tw-opacity-80"
              :height="16"
              @click="removeUserFromSelection(user)"
            />
          </div>
        </div>
      </div>
    </template>

    <!-- Custom footer controls slot for subtasks toggle -->
    <template v-if="showSubItemsCheckbox" #footer-custom-controls>
      <modal-control-toggle
        label="Subtasks"
        :model-value="includeSubtasks"
        :primary-color="finalPrimaryColor"
        hotkey="s"
        aria-label="Toggle inclusion of subtask assignees"
        @update:model-value="handleSubtasksToggle"
      />
    </template>
  </multi-selector-template>
</template>

<script setup lang="ts">
defineOptions({ inheritAttrs: false });
import {
  OrganizationMembership,
  OrganizationMembershipQuery,
  OrganizationMembershipStatus,
} from '@/models/user.models';
import { Colors } from '@/api';
import { ref, watch, nextTick, computed, toRef, Ref } from 'vue';
import { useKeyboardShortcuts } from '@/composables/modal/useKeyboardShortcuts';
import { organizationData } from '@/store/helpers';
import UserService from '@/services/domain/userService';
import UserBrowser from './UserBrowser.vue';
import { UserAvatar } from '@/components/avatars';
import MultiSelectorTemplate from '../../templates/MultiSelectorTemplate.vue';
import EmptySelectionState from '../../shared/EmptySelectionState.vue';
import ModalControlToggle from '../../shared/ModalControlToggle.vue';
import { STANDARD_NAMING } from '../../types/selectorStandards';
import type {
  StandardMultiSelectorProps,
  StandardMultiSelectorEmits,
  StandardBrowserInstance,
  StandardSelectorInstance,
} from '../../types/selectorStandards';
import { useBaseSelector } from '../../composables/useBaseSelector';
import {
  useEnhancedModalLifecycle,
  type BrowserInstance,
} from '../../composables/useBrowserInitialization';

const standardNaming = STANDARD_NAMING;

// Type for UserBrowser - extends standard interface
interface UserBrowserInstance
  extends StandardBrowserInstance<OrganizationMembership> {
  scrollToFirstSelected: () => Promise<boolean | undefined>;
}

// Helper function for user display name
function getUserDisplayName(user: OrganizationMembership): string {
  return user.user?.name || user.user?.email || 'Unknown User';
}

// Helper function to check if user is archived
function isUserArchived(user: OrganizationMembership): boolean {
  return user.status === OrganizationMembershipStatus.INACTIVE;
}

/**
 * Props Interface
 * Extends StandardMultiSelectorProps with user-specific props
 */
interface Props extends StandardMultiSelectorProps<OrganizationMembership> {
  // User-specific props
  showArchivedToggle?: boolean;
  showSubItemsCheckbox?: boolean;
  subItemsChecked?: boolean;
  teamIds?: string[];
  userQuery?: OrganizationMembershipQuery;
}

/**
 * Emits Interface
 * Extends StandardMultiSelectorEmits with user-specific events
 */
type Emits = StandardMultiSelectorEmits<OrganizationMembership> & {
  (e: 'subtasks-changed', includeSubtasks: boolean): void;
};

const props = withDefaults(defineProps<Props>(), {
  primaryColor: Colors.BLUE,
  title: 'Select Users',
  clearable: true,
  actionText: 'Select',
  noItemOption: false,
  noItemText: 'No User',
  showArchivedToggle: true,
  showSubItemsCheckbox: false,
});

const emit = defineEmits<Emits>();
const { shortcuts, withShortcut } = useKeyboardShortcuts();

// Refs
const modalRef = ref<StandardSelectorInstance>();
const userBrowserRef = ref<UserBrowserInstance>();

// Use shared composable for base selector logic
const { orgMembershipCache } = organizationData();
const {
  localSelected,
  hasNoItemSelected: localHasNoUserSelected,
  isLoading,
  loadFromIds,
  clearSelection,
} = useBaseSelector<OrganizationMembership>({
  value: toRef(props, 'value'),
  cache: orgMembershipCache,
  getItemId: UserService.getItemId,
  findInCache: UserService.findInCache,
  isMulti: true,
  isolated: true, // Prevent reactive contamination between parent and modal
});

// Cast to proper type for multi-select
const localSelectedUsers = localSelected as Ref<OrganizationMembership[]>;

// Computed property for user IDs - CRITICAL for reactivity
// Must be a computed property (like MultiLabelSelector), not inline .map()
const localSelectedIds = computed(() =>
  localSelectedUsers.value.map((u) => u.userId)
);

// Additional state
const searchQuery = ref('');
const includeInactiveUsers = ref(false);
const includeSubtasks = ref(false);
const previousSelections = ref<OrganizationMembership[]>([]);

// Computed values
const currentActionText = computed(() => props.actionText ?? 'Select');
const currentNoItemOption = computed(() => props.noItemOption ?? false);
const currentNoItemText = computed(() => props.noItemText ?? 'No User');
const finalShowArchivedToggle = computed(
  () => props.showArchivedToggle ?? true
);

// Final computed values for the template
const finalTitle = computed(() => props.title || 'Select Users');
const finalPrimaryColor = computed(() => props.primaryColor || Colors.BLUE);
const finalClearable = computed(() => props.clearable || false);
const finalActionText = computed(() => {
  const actionText = currentActionText.value;
  if (actionText !== 'Select') {
    return withShortcut(actionText, shortcuts.SAVE);
  }
  const count = localSelectedUsers.value.length;
  const itemText = count === 1 ? 'User' : 'Users';
  const countText = count > 0 ? `${count} ` : '';
  return withShortcut(`Select ${countText}${itemText}`, shortcuts.SAVE);
});

// Computed property to determine if no user option should be shown
const shouldShowNoUserOption = computed(() => currentNoItemOption.value);

// Note: Watch for value prop is handled by useBaseSelector composable

// Store current selections before opening (moved outside lifecycle)
function storeCurrentSelections() {
  // CRITICAL: Store from prop value (source of truth), not from local state
  // The useBaseSelector composable ignores prop changes after initial load in multi-mode,
  // so we must always reference the prop when storing "current" state
  previousSelections.value = props.value ? [...props.value] : [];
}

// Watch for subItemsChecked prop changes
watch(
  () => props.subItemsChecked,
  (checked) => {
    if (checked !== undefined) {
      includeSubtasks.value = checked;
    }
  },
  { immediate: true }
);

// Use enhanced modal lifecycle with integrated browser initialization
const {
  onShow: originalOnShow,
  onHide,
  onShown,
} = useEnhancedModalLifecycle({
  browserOptions: {
    browserRef: userBrowserRef as Ref<BrowserInstance | undefined>,
    getInitParams: () => undefined, // UserBrowser doesn't take params
    onInitialized: async () => {
      // No scrolling needed for multi-select on initialization
      // The selected items are shown in the right panel
    },
    onError: () => {
      // Error handled silently - browser initialization failed
    },
  },
  loadData: loadFromIds,
  emit: emit as (event: string, ...args: unknown[]) => void,
});

// Wrap onShow to force reload and store selections
const onShow = async () => {
  // CRITICAL: Force reload localSelected from props FIRST
  // The useBaseSelector composable ignores prop changes after first initialization in multi-mode
  // (see useBaseSelector.ts lines 138-140), so we must manually sync on each open
  localSelectedUsers.value = props.value ? [...props.value] : [];

  // Store the current state (from props) so we can restore on cancel
  storeCurrentSelections();

  // Initialize browser - this will load items and apply selections from props
  // The browser's useSelectionSync will pick up the selectedUsers prop
  originalOnShow();

  // Wait for browser initialization to complete
  await nextTick();

  // Re-initialize the browser to ensure it has fresh state
  // This clears old selections and re-applies from the selectedUsers prop
  if (userBrowserRef.value) {
    await userBrowserRef.value.initialize();
  }
};

// Search handling
function handleSearch(query: string) {
  searchQuery.value = query;
  userBrowserRef.value?.setSearchQuery(query);
}

// Selection handling
function handleUserSelected(user: OrganizationMembership | null) {
  // In multi mode, this is typically not used (toggle is used instead)
  if (user) {
    // When a user is selected (not toggled), add them to the selection
    handleUserToggled(user, true);
  }
}

function handleUserToggled(user: OrganizationMembership, selected?: boolean) {
  // If selected is undefined, we need to determine the state
  const actualSelected =
    selected !== undefined
      ? selected
      : !localSelectedUsers.value.some((u) => u.userId === user.userId);

  if (actualSelected) {
    // Add the user if not already selected
    const exists = localSelectedUsers.value.some(
      (u) => u.userId === user.userId
    );
    if (!exists) {
      localSelectedUsers.value = [...localSelectedUsers.value, user];
    }
  } else {
    // Remove the user if selected
    const index = localSelectedUsers.value.findIndex(
      (u) => u.userId === user.userId
    );
    if (index >= 0) {
      localSelectedUsers.value = localSelectedUsers.value.filter(
        (u) => u.userId !== user.userId
      );
    }
  }
}

// Template-level handlers for keyboard shortcuts
function handleToggleArchived(value: boolean) {
  includeInactiveUsers.value = value;
}

function handleSelectNone(value: boolean) {
  if (shouldShowNoUserOption.value) {
    localHasNoUserSelected.value = value;
  }
}

// Navigation handlers removed - BaseModal handles via DOM manipulation

function clear() {
  clearSelection();
  emit('update:value', []);
  emit('cleared');
}

function handleSubtasksToggle(value: boolean) {
  includeSubtasks.value = value;
  emit('subtasks-changed', value);
}

function handleToggleSubtasks() {
  // Toggle the subtasks state when 's' key is pressed
  if (props.showSubItemsCheckbox) {
    handleSubtasksToggle(!includeSubtasks.value);
  }
}

function removeUserFromSelection(user: OrganizationMembership) {
  const index = localSelectedUsers.value.findIndex(
    (u) => u.userId === user.userId
  );
  if (index !== -1) {
    // Create a new array instead of mutating
    localSelectedUsers.value = localSelectedUsers.value.filter(
      (u) => u.userId !== user.userId
    );
  }
}

function confirmSelection() {
  // Emit standardized events
  emit('update:value', localSelectedUsers.value);
  emit('selected', localSelectedUsers.value);

  // Emit no-item event if applicable
  if (localHasNoUserSelected.value) {
    emit('no-item-selected');
  }

  // Close the modal
  modalRef.value?.closePopup();
}

function handleCancel() {
  // Restore previous selections on cancel
  localSelectedUsers.value = [...previousSelections.value];
  closePopup();
}

// Focus handling
function handleFocusFirst() {
  userBrowserRef.value?.handleFocusFirst();
}

function handleFocusLast() {
  userBrowserRef.value?.handleFocusLast();
}

async function handleFocusBodyLast() {
  await nextTick();
  userBrowserRef.value?.handleFocusLastFromFooter?.() ||
    userBrowserRef.value?.handleFocusLast();
}

// Public API
function showPopup(trigger?: HTMLElement) {
  modalRef.value?.showPopup(trigger);
}

function closePopup() {
  modalRef.value?.closePopup();
}

defineExpose({
  showPopup,
  closePopup,
  isVisible: computed(() => modalRef.value?.isVisible || false),
});
</script>
